import { ChangeDetectionStrategy, Component, effect, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { formData } from 'src/app/forms/vehicleData';
import { getVehicleForm } from 'src/app/forms/vehicleForms';
import { IOperator, IVehicle, IVehiclePayload } from 'src/app/models';
import { InputType } from 'src/app/models/enums';
import { LiteralService, UsersService, UtilsService, VehiclesService } from 'src/app/services';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';

@Component({
  selector: 'app-vehicle-details',
  templateUrl: './vehicle-details.component.html',
  styleUrl: './vehicle-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VehicleDetailsComponent {

  readonly vehicle = input<IVehicle | undefined>(undefined);
  readonly newVehicle = input<IVehicle | undefined>(undefined);

  readonly closeDetails = output();
  readonly saveVehicle = output<IVehiclePayload>();
  readonly changeStatusEventEmitter = output<boolean>();

  public images = images;
  public InputType = InputType;
  public TooltipPosition = TooltipPosition;

  vehicleForm = signal<formData[]>([]);
  public vehicleCopy = {} as IVehicle;
  public edit = false;
  public editStatus = false;
  public invalid: string[] = [];
  public statusList = [
    { id: 'active', name: 'active' },
    { id: 'inactive', name: 'inactive' },
  ];

  private originalVehicle = {} as IVehicle;

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
    private vehiclesService: VehiclesService,
    private usersService: UsersService
  ) {
    effect(() => {
      this.vehicleCopy = structuredClone(this.vehicle() ?? this.newVehicle() ?? ({} as IVehicle));
      this.originalVehicle = structuredClone(this.vehicleCopy);
      this.edit = this.newVehicle() ? true : false;
      this.invalid = [];
      this.getForm();
    });
  }

  private async getForm() {
    this.vehicleForm.set(
      await getVehicleForm(this.vehicleCopy, this.literalService, this.vehiclesService, this.usersService, this.edit),
    );
  }

  getTotalStandardSeats() {
    const convertibleSeats = this.vehicleCopy.convertibleSeats ?? [];
    const standardSeats = this.vehicleCopy.availableSeats;
    if (convertibleSeats.length === 0) return standardSeats;
    const convertibleStandardSeats = convertibleSeats.reduce((acc, seat) => acc + (seat.numberOfRegularSeats || 0), 0);
    return `${standardSeats} - ${standardSeats + convertibleStandardSeats}`
  }

  getTotalPrmSeats() {
    const convertiblePRMSeats = this.vehicleCopy.convertibleSeats?.length ?? 0;
    const prmSeats = this.vehicleCopy.prmAvailableSeats;
    if (convertiblePRMSeats === 0) return prmSeats;
    return `${prmSeats} - ${prmSeats + convertiblePRMSeats}`
  }

  public editAction() {
    if (this.edit) {
      this.cancel();
      return;
    }
    this.edit = true;
    this.getForm();
  }

  public vehicleStatus() {
    return this.vehicleCopy.active ? 'active' : 'inactive';
  }

  public editStatusClick() {
    this.editStatus = !this.editStatus;
  }

  public changeStatus(status?: string) {
    this.editStatus = false;
    const active = status === 'active';
    if (!status || active === this.vehicleCopy.active) return;
    this.changeStatusEventEmitter.emit(active);
  }

  public cancel() {
    if (this.newVehicle()) {
      this.close();
      return;
    }
    this.vehicleCopy = structuredClone(this.originalVehicle);
    this.invalid = [];
    this.edit = false;
    this.getForm();
  }

  public close() {
    this.edit = false;
    this.closeDetails.emit();
  }

  public onInputChange(event: any, element: formData) {
    const field = element.edit?.valueToChange;
    if (!field) return;
    const value = this.getValue(event.target ?? event, element);
    (this.vehicleCopy as any)[field] = value;
    element.value = value;
    this.findInvalidControls();
  }

  private getValue(target: any, element: formData) {
    switch (element.edit?.inputType) {
      case InputType.NUMBER:
        return target.value === '' ? undefined : Number(target.value);
      default:
        return target.value;
    }
  }

  public findInvalidControls() {
    const controls = this.initFormControl().controls;
    this.invalid = Object.keys(controls).filter((name) => controls[name].invalid);
  }

  private initFormControl(): FormGroup {
    return new FormGroup({
      name: new FormControl(this.vehicleCopy.name, [Validators.required, Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/)]),
      operator: new FormControl(this.vehicleCopy.operator, [Validators.required]),
      availableSeats: new FormControl(this.vehicleCopy.availableSeats, [Validators.required, Validators.min(0)]),
      prmAvailableSeats: new FormControl(this.vehicleCopy.prmAvailableSeats, [Validators.required, Validators.min(0)]),
      convertibleSeats: new FormControl(this.vehicleCopy.convertibleSeats),
    });
  }

  public showError(field: string) {
    if (!this.invalid.includes(field)) return '';
    const value = (this.vehicleCopy as any)[field];
    return value === undefined || value === null || value === ''
      ? this.literalService.get('errors.requiredField', true)
      : this.literalService.get('errors.incorrectField', true);
  }

  public disabledButton() {
    if (!this.initFormControl().valid) return true;
    return !this.newVehicle() && this.utilsService.compareObjects(this.vehicleCopy, this.originalVehicle).length === 0;
  }

  private processPrmSeats() {
    const convertibleSeats = structuredClone(this.vehicleCopy.convertibleSeats) ?? [];
    const prmSeatsExclusive = this.vehicleCopy.prmAvailableSeats ?? 0;
    for (let seat = 0; seat < prmSeatsExclusive; seat++) {
      convertibleSeats.push({ numberOfRegularSeats: 0 });
    }
    return convertibleSeats;
  }

  public onSubmit() {
    this.findInvalidControls();
    if (this.invalid.length > 0) return;
    const vehiclePayload: IVehiclePayload = {
      name: this.vehicleCopy.name,
      operatorId: this.vehicleCopy.operator?.id,
      availableSeats: this.vehicleCopy.availableSeats,
      convertibleSeats: this.processPrmSeats()
    }
    this.saveVehicle.emit(vehiclePayload);
  }
}
